import React, { useRef } from 'react';

import { useOnClickOutside } from 'usehooks-ts';

import type { SxProps, Theme } from '@mui/material';
import { Box, Dialog, DialogContent, DialogTitle, styled, Typography } from '@mui/material';

import { CloseIconButton } from 'Common/CloseIconButton';
import { Colors } from 'Themes/colors';
import { Spacing } from 'Themes/spacing';

const StyledDialog = styled(Dialog)<{ maxwidth?: string }>`
  .MuiPaper-root {
    max-width: ${({ maxwidth }) => maxwidth ?? '500px'};
    width: 100%;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.medium} ${Spacing.medium} ${Spacing.small} ${Spacing.medium};
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 0 ${Spacing.medium} ${Spacing.medium} ${Spacing.medium} !important;
`;

const DropdownContainer = styled(Box)<{ dropdownPosition?: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  ${({ dropdownPosition }) => (dropdownPosition === 'left' ? 'left: 0;' : 'right: 0;')}
  z-index: 2;
  background: ${Colors.white};
  border: 2px solid ${Colors.textColor};
  border-radius: 7px;
  display: flex;
  padding: ${Spacing.medium};
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: ${Spacing.medium};
`;

const DropdownContent = styled(Box)``;

const dropdownIconButtonSx = {
  margin: '-14px !important'
};

const styledIconButtonSx = {
  margin: '-8px !important'
};

const PositionedContainer = styled(Box)<{ maxwidth?: string }>`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1500;
  background: ${Colors.white};
  border: 2px solid ${Colors.textColor};
  border-radius: 7px;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.2);
  max-width: ${({ maxwidth }) => maxwidth ?? '500px'};
  width: auto;
  max-height: 80vh;
  overflow-y: auto;
  margin-top: 8px;
`;

const PositionedHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.medium} ${Spacing.medium} ${Spacing.small} ${Spacing.medium};
`;

const PositionedContent = styled(Box)`
  padding: 0 ${Spacing.medium} ${Spacing.medium} ${Spacing.medium};
`;

const PositionedTitle = styled(Typography)`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
` as typeof Typography;

type BaseModalProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  variant?: 'dialog' | 'dropdown' | 'positioned';
  maxWidth?: string;
  fullWidth?: boolean;
  dropdownPosition?: 'left' | 'right';
  sx?: SxProps<Theme>;
};

export const BaseModal = ({
  open,
  onClose,
  children,
  title,
  variant = 'dialog',
  maxWidth = '500px',
  fullWidth = true,
  dropdownPosition = 'left',
  sx
}: Readonly<BaseModalProps>) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(
    dropdownRef as React.RefObject<HTMLElement>,
    variant === 'dropdown' ? onClose : () => {}
  );

  if (variant === 'dropdown') {
    return (
      <DropdownContainer ref={dropdownRef} dropdownPosition={dropdownPosition}>
        <DropdownContent>{children}</DropdownContent>
        <CloseIconButton onClose={onClose} sx={dropdownIconButtonSx} />
      </DropdownContainer>
    );
  }

  if (variant === 'positioned') {
    return (
      <PositionedContainer maxwidth={maxWidth}>
        {title && (
          <PositionedHeader>
            <PositionedTitle>{title}</PositionedTitle>
            <CloseIconButton onClose={onClose} sx={styledIconButtonSx} />
          </PositionedHeader>
        )}
        <PositionedContent>{children}</PositionedContent>
      </PositionedContainer>
    );
  }

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={fullWidth}
      maxwidth={maxWidth}
      sx={sx}
    >
      {title && (
        <StyledDialogTitle>
          {title}
          <CloseIconButton onClose={onClose} sx={styledIconButtonSx} />
        </StyledDialogTitle>
      )}

      <StyledDialogContent>{children}</StyledDialogContent>
    </StyledDialog>
  );
};
